import {
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from "react";
import { Command } from "../command";
import { cn } from "../../../lib/utils";
import { useMenuNavMode } from "./use-menu-nav-mode";
import {
  buildAccountMenuModel,
  DEFAULT_UNGROUPED_LABEL,
} from "./account-menu-model";
import {
  setGroupCollapsed,
  toggleGroupCollapsed,
  useCollapsedGroups,
} from "./group-collapse-store";
import {
  AccountMenuItem,
  GroupHeader,
  groupRowAccessibleName,
  Highlighted,
  MenuCheckbox,
  MenuEmptyState,
  type SelectionState,
} from "./menu-parts";
import type { SelectorAccount, SelectorGroup } from "./types";
import type { SelectorSize } from "./types";

export interface AccountMultiSelectMenuProps {
  accounts: SelectorAccount[];
  groups?: SelectorGroup[];
  selectedIds: string[];
  onChange: (selectedIds: string[]) => void;
  /** Always shown when true, which is the default. Unlike the single-select
   * menu, this one does NOT auto-hide the search below six accounts: a
   * multi-select is normally reached from a form where the box is expected,
   * and hiding it there reads as a missing control rather than as tidiness. */
  searchable?: boolean;
  searchPlaceholder?: string;
  emptyMessage?: string;
  selectAllLabel?: string;
  ungroupedLabel?: string;
  showSelectAll?: boolean;
  size?: SelectorSize;
  className?: string;
  listClassName?: string;
}

function checkboxState(ids: string[], selected: Set<string>) {
  const selectedCount = ids.filter((id) => selected.has(id)).length;
  if (selectedCount === 0) return false;
  if (selectedCount === ids.length) return true;
  return "indeterminate" as const;
}

function MultiSelectMenuButton({
  checked,
  disabled,
  title,
  label,
  sectionId,
  id,
  active = false,
  className,
  onClick,
  children,
}: {
  checked: SelectionState;
  disabled?: boolean;
  title?: string;
  label: string;
  sectionId?: string;
  /** The listbox names its active option through `aria-activedescendant`,
   * which needs an id to point at. */
  id?: string;
  /** The row the arrow keys are on. Not DOM focus: a listbox is a single tab
   * stop, so its options are skipped by the browser. */
  active?: boolean;
  className?: string;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      role="option"
      id={id}
      // Skipped by the browser on purpose: the listbox owns the tab stop.
      tabIndex={-1}
      // `aria-selected` has no mixed value; the name carries the partial state.
      aria-selected={checked === true}
      data-selected={active ? "true" : undefined}
      aria-label={label}
      data-group-header=""
      data-section-id={sectionId}
      disabled={disabled}
      data-disabled={disabled || undefined}
      title={title}
      onClick={onClick}
      className={cn(
        "relative flex w-full cursor-pointer items-center gap-2 rounded-none px-3.5 text-left font-inter text-sm font-medium text-neutral-800 outline-hidden select-none hover:bg-neutral-50 data-[selected=true]:bg-neutral-50 group-data-[nav=keyboard]/menu:data-[selected=true]:ring-1 group-data-[nav=keyboard]/menu:data-[selected=true]:ring-inset group-data-[nav=keyboard]/menu:data-[selected=true]:ring-blue-500 disabled:pointer-events-none disabled:opacity-50",
        className
      )}
    >
      {children}
    </button>
  );
}

function MultiSelectSearchInput({
  value,
  onChange,
  placeholder,
  controls,
  activeId,
  size,
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  controls: string;
  /** This input is the listbox's focus host while a search box exists, so it
   * is the element that has to name the active option. */
  activeId?: string;
  size: SelectorSize;
}) {
  return (
    <div
      className={cn(
        "flex shrink-0 items-center gap-2 border-b border-neutral-100",
        size === "md" ? "h-10 px-3.5" : "h-8 px-3"
      )}
    >
      <i
        aria-hidden
        className="fa-regular fa-magnifying-glass size-5 shrink-0 text-center text-[16px] leading-5 text-neutral-500"
      />
      <input
        type="text"
        role="searchbox"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === "Home" || event.key === "End") {
            event.stopPropagation();
          }
        }}
        aria-controls={controls}
        aria-activedescendant={activeId}
        // A placeholder is not an accessible name: it disappears the moment
        // anything is typed.
        aria-label={placeholder || "Search accounts"}
        placeholder={placeholder}
        className="h-full w-full rounded-md bg-transparent py-0 font-inter text-sm outline-hidden placeholder:text-neutral-500"
      />
    </div>
  );
}

function CollapseChevron({
  label,
  collapsed,
  onToggle,
  className,
}: {
  label: string;
  collapsed: boolean;
  onToggle: () => void;
  className?: string;
}) {
  return (
    <button
      type="button"
      // Not a tab stop: a focusable non-option inside a listbox breaks the
      // single-tab-stop contract. ArrowRight on the group row is the route.
      tabIndex={-1}
      aria-label={`${collapsed ? "Expand" : "Collapse"} ${label}`}
      aria-expanded={!collapsed}
      onKeyDown={(event) => {
        if (event.key !== "Enter" && event.key !== " ") return;
        event.preventDefault();
        event.stopPropagation();
        onToggle();
      }}
      onClick={(event) => {
        event.preventDefault();
        event.stopPropagation();
        onToggle();
      }}
      className={cn(
        "inline-flex size-5 shrink-0 items-center justify-center rounded text-neutral-500 hover:bg-neutral-50 hover:text-neutral-800",
        className
      )}
    >
      <i
        aria-hidden
        className={cn(
          "fa-regular fa-chevron-down text-[12px] leading-none transition-transform",
          collapsed && "-rotate-90"
        )}
      />
    </button>
  );
}

export function AccountMultiSelectMenu({
  accounts,
  groups = [],
  selectedIds,
  onChange,
  searchable = true,
  searchPlaceholder = "Search accounts...",
  emptyMessage = "No accounts found.",
  selectAllLabel = "Select all",
  ungroupedLabel = DEFAULT_UNGROUPED_LABEL,
  showSelectAll = true,
  size = "md",
  className,
  listClassName,
}: AccountMultiSelectMenuProps) {
  const [search, setSearch] = useState("");
  const [activeId, setActiveId] = useState<string | null>(null);
  const pendingAnchorRef = useRef<string | null>(null);

  const listRef = useRef<HTMLDivElement>(null);
  const listId = useId();
  const optionId = (key: string) => `${listId}-opt-${key}`;
  const collapsedGroupIds = useCollapsedGroups();
  useLayoutEffect(() => {
    const section = pendingAnchorRef.current;
    if (!section) return;
    pendingAnchorRef.current = null;
    // Compared as a string, never interpolated: a group with no record
    // behind it uses its own brand name as its id, so a name holding a quote
    // built an invalid selector and threw out of this effect.
    const header = Array.from(
      listRef.current?.querySelectorAll<HTMLElement>(
        '[role="option"][data-section-id]'
      ) || []
    ).find((element) => element.dataset.sectionId === section);
    if (header?.id) setActiveId(header.id);
  }, [collapsedGroupIds]);
  const model = useMemo(
    () =>
      buildAccountMenuModel({
        accounts,
        groups,
        query: search,
        collapsedGroupIds,
        ungroupedLabel,
        namedGroupHeaders: true,
      }),
    [accounts, groups, search, collapsedGroupIds, ungroupedLabel]
  );

  const visibleIds = model.visibleAccounts
    .filter((account) => !account.disabled)
    .map((account) => account.id);
  const visibleSelectAllLabel = model.hasQuery
    ? `Select all ${visibleIds.length} results`
    : selectAllLabel;
  const selected = new Set(selectedIds);

  const toggleIds = (ids: string[]) => {
    const next = new Set(selectedIds);
    const allSelected = ids.length > 0 && ids.every((id) => next.has(id));
    ids.forEach((id) => (allSelected ? next.delete(id) : next.add(id)));
    onChange(Array.from(next));
  };

  const toggleAccount = (account: SelectorAccount) => {
    if (account.disabled) return;
    toggleIds([account.id]);
  };

  const { navMode, navHandlers } = useMenuNavMode();

  /** Every option in DOM order, which is the order the arrows travel. */
  const optionElements = () =>
    Array.from(
      listRef.current?.querySelectorAll<HTMLButtonElement>(
        '[role="option"]:not(:disabled)'
      ) || []
    );

  const moveActive = (resolve: (items: HTMLButtonElement[]) => number) => {
    const items = optionElements();
    if (items.length === 0) return;
    const next = items[resolve(items)];
    if (!next?.id) return;
    setActiveId(next.id);
    next.scrollIntoView({ block: "nearest" });
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    navHandlers.onKeyDown(event);
    const active = activeId ? document.getElementById(activeId) : null;

    if (event.key === "Enter" || event.key === " ") {
      // The row no longer holds focus, and Space would otherwise scroll.
      if (!active) return;
      if (event.key === " " && event.target instanceof HTMLInputElement) return;
      event.preventDefault();
      event.stopPropagation();
      (active as HTMLButtonElement).click();
      return;
    }

    if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
      const target = event.target as HTMLElement | null;
      if (target instanceof HTMLInputElement && target.value.length > 0) return;
      if (model.hasQuery) return;
      const sectionId = active?.dataset.sectionId;
      const section = model.sections.find((entry) => entry.id === sectionId);
      if (!sectionId || !section?.showHeader) return;
      event.preventDefault();
      const collapsing = event.key === "ArrowLeft";
      setGroupCollapsed(sectionId, collapsing);
      // Collapsing removes the row the highlight is on, so it moves to the
      // header or ArrowRight has nothing to reopen. Resolved from the DOM
      // afterwards because a cmdk heading carries an id cmdk assigns itself.
      if (collapsing) pendingAnchorRef.current = sectionId;
      return;
    }

    const arrowKey = event.key === "ArrowDown" || event.key === "ArrowUp";
    const edgeKey = event.key === "Home" || event.key === "End";
    if (!arrowKey && !edgeKey) return;

    event.preventDefault();
    event.stopPropagation();
    moveActive((items) => {
      if (event.key === "Home") return 0;
      if (event.key === "End") return items.length - 1;
      const currentIndex = active
        ? items.indexOf(active as HTMLButtonElement)
        : -1;
      if (event.key === "ArrowUp") {
        return currentIndex <= 0 ? items.length - 1 : currentIndex - 1;
      }
      return currentIndex >= items.length - 1 ? 0 : currentIndex + 1;
    });
  };

  return (
    <Command
      shouldFilter={false}
      data-selector-menu=""
      label="Account selection"
      onKeyDown={handleKeyDown}
      onPointerMove={navHandlers.onPointerMove}
      data-nav={navMode}
      className={cn(
        // `group/menu` publishes the nav mode to the rows; `overflow-hidden`
        // stops square row fills escaping the rounded corners.
        "group/menu overflow-hidden",
        "h-auto max-h-[min(60vh,var(--radix-popover-content-available-height,60vh))] rounded-lg border-neutral-100 py-1 shadow-[0_4px_8px_-2px_rgba(16,24,40,0.10),0_2px_4px_-2px_rgba(16,24,40,0.06)]",
        className
      )}
    >
      {searchable ? (
        <MultiSelectSearchInput
          value={search}
          onChange={setSearch}
          placeholder={searchPlaceholder}
          controls={listId}
          activeId={activeId ?? undefined}
          size={size}
        />
      ) : null}
      <div
        ref={listRef}
        id={listId}
        role="listbox"
        aria-multiselectable="true"
        aria-label="Accounts"
        aria-activedescendant={activeId ?? undefined}
        tabIndex={searchable ? -1 : 0}
        className={cn(
          "min-h-0 flex-1 scroll-pt-11 overflow-x-hidden overflow-y-auto",
          listClassName
        )}
      >
        {model.visibleAccounts.length === 0 ? (
          <MenuEmptyState
            query={search}
            message={emptyMessage}
            onClear={() => setSearch("")}
          />
        ) : null}
        {showSelectAll && visibleIds.length > 0 ? (
          <div role="presentation" className="p-0">
            <MultiSelectMenuButton
              checked={checkboxState(visibleIds, selected)}
              id={optionId("select-all")}
              active={activeId === optionId("select-all")}
              label={groupRowAccessibleName(
                visibleSelectAllLabel,
                checkboxState(visibleIds, selected)
              )}
              onClick={() => toggleIds(visibleIds)}
              // Matches the single menu's "All accounts" row: same place
              // in the hierarchy, so the same height.
              className={cn("h-11", size === "md" ? "py-2.5" : "py-1.5")}
            >
              <MenuCheckbox checked={checkboxState(visibleIds, selected)} />
              <span className="truncate">{visibleSelectAllLabel}</span>
            </MultiSelectMenuButton>
          </div>
        ) : null}
        {model.sections.map((section) => {
          const showGroupRow = section.showHeader;
          // A group with nothing behind it is still a heading: its members are
          // listed and it still collapses, there is just nothing to toggle.
          const selectableGroup = section.group?.selectable !== false;
          const groupIds = section.matched
            .filter((account) => !account.disabled)
            .map((account) => account.id);
          const groupChecked = checkboxState(groupIds, selected);
          return (
            <div
              role="presentation"
              key={section.id}
              className={cn(
                "border-t border-neutral-100 p-0",
                size === "md" ? "pb-2.5" : "pb-1.5"
              )}
            >
              {showGroupRow && !selectableGroup ? (
                <GroupHeader
                  label={section.label}
                  sectionId={section.id}
                  count={section.matchedCount}
                  collapsed={section.collapsed}
                  query={search}
                  size={size}
                  // Collapsed, this caption is the only thing left that can
                  // reopen its own section, so it has to be reachable. Expanded
                  // it is a caption again and stays out of the option list.
                  interactive={section.collapsed}
                  value={`__group__ ${section.id}`}
                  onSelect={() => toggleGroupCollapsed(section.id)}
                  onToggleCollapse={
                    model.hasQuery
                      ? undefined
                      : () => toggleGroupCollapsed(section.id)
                  }
                />
              ) : null}
              {showGroupRow && selectableGroup ? (
                // The chevron is a sibling of the option, not a child of it: a
                // <button> inside a <button> is invalid markup, and an
                // interactive descendant inside `role="option"` breaks the
                // listbox contract. The wrapper carries the sticky positioning
                // so the row still pins while its members scroll.
                <div
                  className={cn(
                    "sticky top-0 z-10 flex items-center bg-white",
                    // Same scale as the single menu's selectable group
                    // header, so the two menus read as one control family.
                    "h-11"
                  )}
                >
                  <MultiSelectMenuButton
                    checked={groupChecked}
                    sectionId={section.id}
                    id={optionId(`group-${section.id}`)}
                    active={activeId === optionId(`group-${section.id}`)}
                    disabled={section.group?.disabled || groupIds.length === 0}
                    title={section.group?.disabledReason}
                    label={groupRowAccessibleName(section.label, groupChecked)}
                    onClick={() => {
                      if (!section.group?.disabled) toggleIds(groupIds);
                    }}
                    className={cn(
                      "min-w-0 flex-1",
                      "h-11",
                      size === "md" ? "py-2.5" : "py-1.5",
                      model.hasQuery ? undefined : "pr-1"
                    )}
                  >
                    <MenuCheckbox checked={groupChecked} />
                    <span className="min-w-0 truncate">
                      <Highlighted text={section.label} query={search} />
                    </span>
                    <span className="shrink-0 text-xs font-normal text-neutral-400">
                      {`· ${section.matchedCount}`}
                    </span>
                    <span className="flex-1" />
                  </MultiSelectMenuButton>
                  {model.hasQuery ? null : (
                    <CollapseChevron
                      label={section.label}
                      collapsed={section.collapsed}
                      onToggle={() => toggleGroupCollapsed(section.id)}
                      className={cn(size === "md" ? "mr-3.5" : "mr-3")}
                    />
                  )}
                </div>
              ) : null}
              {section.accounts.map((account) => (
                <AccountMenuItem
                  key={account.id}
                  account={account}
                  multi
                  id={optionId(`account-${account.id}`)}
                  active={activeId === optionId(`account-${account.id}`)}
                  keyboardNav={navMode === "keyboard"}
                  keyboardActive={
                    navMode === "keyboard" &&
                    activeId === optionId(`account-${account.id}`)
                  }
                  sectionId={section.id}
                  nested={showGroupRow}
                  size={size}
                  query={search}
                  selected={selected.has(account.id)}
                  onSelect={() => toggleAccount(account)}
                />
              ))}
            </div>
          );
        })}
      </div>
    </Command>
  );
}
